import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { getAffilmasterKlienti, type KlientListItem } from '../api/affilmaster';
import { fitLogo, logoSrc, newPromoItem, validPromoUrl, type PromoItem } from './promo-news';

function measure(src: string): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export function PromoNewsEditor({ country, items, onChange }: { country: string; items: PromoItem[]; onChange: Dispatch<SetStateAction<PromoItem[]>> }) {
  const [clients, setClients] = useState<KlientListItem[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAffilmasterKlienti()
      .then((list) => !cancelled && setClients(list.filter((c) => c.active).sort((a, b) => a.name.localeCompare(b.name, 'cs'))))
      .catch(() => !cancelled && setError('Seznam klientů se nepodařilo načíst.'));
    return () => { cancelled = true; };
  }, []);

  const offered = useMemo(() => (showAll ? clients : clients.filter((c) => c.market === country)), [clients, showAll, country]);

  const update = (key: string, patch: Partial<PromoItem>) => onChange((cur) => cur.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const remove = (key: string) => onChange((cur) => cur.filter((it) => it.key !== key));
  const move = (index: number, dir: -1 | 1) => onChange((cur) => {
    const target = index + dir;
    if (target < 0 || target >= cur.length) return cur;
    const next = [...cur];
    [next[index], next[target]] = [next[target]!, next[index]!];
    return next;
  });

  async function pickClient(key: string, id: number | null) {
    const c = clients.find((x) => x.id === id) ?? null;
    if (!c) { update(key, { clientId: null, name: '', logo: null, logoW: null, logoH: null }); return; }
    update(key, { clientId: c.id, name: c.name, logo: c.logo, logoW: null, logoH: null });
    const size = c.logo ? await measure(c.logo) : null;
    onChange((cur) => cur.map((it) => (it.key === key && it.clientId === c.id ? { ...it, logoW: size?.w ?? null, logoH: size?.h ?? null } : it)));
  }

  return (
    <div className="promo-editor">
      <div className="promo-editor-head">
        <label className="form-check form-switch d-inline-flex align-items-center gap-2 mb-0">
          <input className="form-check-input" type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          <span className="small">Nabízet i klienty jiných trhů</span>
        </label>
        {error && <span className="text-danger small">{error}</span>}
      </div>
      {items.map((it, i) => {
        const inList = offered.some((c) => c.id === it.clientId);
        const current = !inList && it.clientId != null ? clients.find((c) => c.id === it.clientId) : null;
        const urlBad = it.url.trim() !== '' && !validPromoUrl(it.url);
        return (
          <div className="promo-row" key={it.key}>
            <div className="promo-row-logo">{it.logo ? <img src={logoSrc(it.logo) ?? it.logo} alt={it.name} width={fitLogo(it.logoW, it.logoH, 76, 40)?.w} height={fitLogo(it.logoW, it.logoH, 76, 40)?.h} /> : <span>{i + 1}</span>}</div>
            <div className="promo-row-fields">
              <div className="promo-row-line">
                <select className="form-select form-select-sm" value={it.clientId ?? ''} onChange={(e) => void pickClient(it.key, e.target.value === '' ? null : Number(e.target.value))}>
                  <option value="">- Vyber klienta -</option>
                  {current && <option value={current.id}>{current.name} ({current.market})</option>}
                  {offered.map((c) => <option key={c.id} value={c.id}>{c.name}{showAll ? ` (${c.market})` : ''}</option>)}
                </select>
                <input className="form-control form-control-sm" value={it.name} onChange={(e) => update(it.key, { name: e.target.value })} placeholder="Název v e-mailu" />
              </div>
              <input className="form-control form-control-sm" value={it.text} onChange={(e) => update(it.key, { text: e.target.value })} placeholder="např. Nové promo - týden 39" maxLength={120} />
              <input className={`form-control form-control-sm${urlBad ? ' is-invalid' : ''}`} value={it.url} onChange={(e) => update(it.key, { url: e.target.value })} placeholder="Odkaz na detail v promo centru, https://podpora.affilmaster.cz/…" />
            </div>
            <div className="promo-row-actions">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Posunout nahoru" aria-label="Posunout nahoru">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Posunout dolů" aria-label="Posunout dolů">↓</button>
              <button type="button" className="is-danger" onClick={() => remove(it.key)} title="Odebrat" aria-label="Odebrat">×</button>
            </div>
          </div>
        );
      })}
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onChange((cur) => [...cur, newPromoItem()])}>+ Přidat klienta</button>
    </div>
  );
}
